import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { Trophy, Package } from "lucide-react";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function RankingUnidade() {
  const { unidade, theme } = useOutletContext();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/relatorios/ranking-unidade", { params: { unidade_id: unidade.id } })
      .then((r) => setData(r.data));
  }, [unidade.id]);

  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  const fonte = data.periodo.fonte;
  const periodoLabel =
    fonte === "meta_ativa"
      ? `Meta ativa: ${data.periodo.meta_titulo}`
      : "Últimos 30 dias (sem meta ativa)";
  const periodoRange = `${new Date(data.periodo.data_inicio + "T00:00").toLocaleDateString(
    "pt-BR",
  )} → ${new Date(data.periodo.data_fim + "T00:00").toLocaleDateString("pt-BR")}`;

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Performance · {unidade.nome}
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Ranking
        </h2>
      </div>

      <div className="bg-white border-2 border-neutral-900 p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Período considerado
          </div>
          <div className="font-display font-black text-lg">{periodoLabel}</div>
          <div className="font-mono text-xs text-neutral-600">{periodoRange}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Caixas fechados
          </div>
          <div className="font-display font-black text-2xl">
            {data.qtd_vendas} <span className="text-sm">vendas</span>
          </div>
        </div>
      </div>

      <p className="font-mono text-[11px] text-neutral-500 mb-4">
        * Considera apenas vendas que já foram consolidadas em fechamentos de caixa.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Ranking de Vendedores" icon={Trophy} accent={theme.primary}>
          {data.ranking_vendedores.length === 0 ? (
            <p className="text-sm text-neutral-500 p-4">
              Sem vendas consolidadas no período.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {data.ranking_vendedores.map((v, idx) => (
                <li
                  key={v.vendedor_id}
                  data-testid={`unidade-rank-vend-${v.vendedor_id}`}
                  className="p-3 flex items-center gap-3"
                >
                  <span className={`${theme.primary} w-7 h-7 flex items-center justify-center font-display font-black text-sm`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{v.nome}</div>
                    <div className="font-mono text-xs text-neutral-600">
                      {v.qtd_vendas} vendas · {money(v.faturamento)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-xl">{v.itens_vendidos}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                      itens
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Ranking de Itens Vendidos" icon={Package} accent={theme.primary}>
          {data.ranking_itens.length === 0 ? (
            <p className="text-sm text-neutral-500 p-4">
              Sem itens consolidados no período.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {data.ranking_itens.slice(0, 15).map((it, idx) => (
                <li
                  key={it.produto_id}
                  data-testid={`unidade-rank-item-${it.produto_id}`}
                  className="p-3 flex items-center gap-3"
                >
                  <span className={`${theme.primary} w-7 h-7 flex items-center justify-center font-display font-black text-sm`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{it.nome}</div>
                    <div className="font-mono text-xs text-neutral-600">
                      {money(it.faturamento)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-xl">{it.quantidade}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                      un
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, accent, children }) {
  return (
    <div className="bg-white border-2 border-neutral-900">
      <div className="p-3 border-b-2 border-neutral-900 flex items-center gap-2">
        <span className={`${accent} w-8 h-8 flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <h3 className="font-display font-bold uppercase tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}
